/**
 * Convert API/client errors into display-safe text.
 *
 * Network clients can expose server payloads as strings, nested objects, or
 * arrays. React must only receive text, never an arbitrary response object.
 */

function firstMessage(value: unknown): string | null {
  if (typeof value === "string") {
    const message = value.trim();
    return message || null;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const message = firstMessage(item);
      if (message) return message;
    }
    return null;
  }

  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    // `msg` is FastAPI/Pydantic's per-error field on a raw 422 detail item
    // ({type, loc, msg, input, ctx}); reading it last lets a validation error
    // show its real reason ("String should have at least 8 characters")
    // instead of the generic fallback. `input`/`ctx` are deliberately NOT read
    // (they can echo the submitted value).
    return (
      firstMessage(record.message) ??
      firstMessage(record.detail) ??
      firstMessage(record.msg)
    );
  }

  return null;
}

/**
 * Return a primitive error message from Axios-style response data or Error.
 * The fallback is used whenever the payload has no safe human-readable text.
 */
export function toUserSafeErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const record = error as Record<string, unknown>;
    const response = record.response;
    if (response && typeof response === "object") {
      const data = (response as Record<string, unknown>).data;
      const message = firstMessage(data);
      if (message) return message;
    }

    const message = firstMessage(record.message);
    if (message) return message;
  }

  return fallback;
}
